import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import { useIsMobile } from '@/core/hooks/use-mobile';

/**
 * DnD sensors for profile edit:
 * - Desktop: drag starts after small movement (distance).
 * - Mobile: long-press on card starts drag; tap + drag on the grip (data-dnd-grip) starts immediately (bypass).
 * Keyboard sensor for accessibility.
 */
export function useDndProfileSensors() {
  const isMobile = useIsMobile();
  return useSensors(
    useSensor(PointerSensor, {
      activationConstraint: isMobile
        ? // delay: user must hold 300 ms before drag activates.
          // tolerance: allow up to 10 px movement during the delay without
          // cancelling — this is needed because fingers never stay perfectly
          // still. A quick swipe fires pointercancel before the 300 ms are up,
          // so the browser handles it as a native scroll gesture instead.
          { delay: 300, tolerance: 10 }
        : { distance: 8 },
      // On mobile, elements marked with data-dnd-grip bypass the delay so
      // that a tap-then-drag from the grip handle starts immediately.
      bypassActivationConstraint: isMobile
        ? ({ event }) =>
            !!(event.target as Element | null)?.closest('[data-dnd-grip]')
        : undefined,
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      // Don't use Space to start drag so that typing space in inputs doesn't trigger DnD
      keyboardCodes: {
        start: ['Enter'],
        cancel: ['Escape'],
        end: ['Enter'],
      },
    }),
  );
}
